import React, { useEffect, useMemo, useState } from "react";
import { confirmarSistema } from "../Components/PopupSistema/PopupSistema";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch
} from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../Config/firebase";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";

const COLECAO_CAIXINHA = "tblCaixinhaEmpresa";
const MES_INICIO_CONTROLE = "2026-09";
const TIPOS_ENTRADA = {
  deposito: "Depósito",
  cartao_alimentacao: "Cartão alimentação",
  saldo_anterior: "Saldo anterior"
};
const CARTEIRAS = {
  dinheiro: "Dinheiro",
  cartao: "Cartão"
};

const formularioInicial = {
  tipo: "saida",
  tipoEntrada: "deposito",
  carteira: "dinheiro",
  data: new Date().toISOString().slice(0, 10),
  descricao: "",
  valor: "",
  numeroNota: ""
};

function moeda(valor) {
  return Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

function formatarDataBR(data) {
  const [ano, mes, dia] = String(data || "").split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : data || "-";
}

function parseValor(valor) {
  if (typeof valor === "number") return valor;
  const normalizado = String(valor || "")
    .replace(/\./g, "")
    .replace(",", ".")
    .replace(/[^\d.-]/g, "");
  return Number(normalizado || 0);
}

function nomeArquivoSeguro(nome) {
  return String(nome || "nota-fiscal")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 120);
}

function carteiraDaEntrada(tipoEntrada) {
  if (tipoEntrada === "saldo_anterior") return null;
  return tipoEntrada === "cartao_alimentacao" ? "cartao" : "dinheiro";
}

function descricaoSaldoAnterior(carteira) {
  return `Saldo anterior - ${CARTEIRAS[carteira] || "Dinheiro"}`;
}

function calcularResumo(lancamentos) {
  return lancamentos.reduce(
    (acc, lancamento) => {
      const carteira = lancamento.carteira === "cartao" ? "cartao" : "dinheiro";
      const valor = Number(lancamento.valor || 0);

      if (lancamento.tipo === "entrada") {
        acc[carteira] += valor;
        acc.totalEntradas += valor;
        acc.saldo += valor;
      } else {
        acc[carteira] -= valor;
        acc.totalSaidas += valor;
        acc.saldo -= valor;
      }

      return acc;
    },
    { dinheiro: 0, cartao: 0, totalEntradas: 0, totalSaidas: 0, saldo: 0 }
  );
}

function mesAnterior(mes) {
  const [ano, numeroMes] = mes.split("-").map(Number);
  const data = new Date(ano, numeroMes - 2, 1);
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}`;
}

export default function CadastroCaixinhaEmpresa({ open, isMobile }) {
  const [lancamentos, setLancamentos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [lancamentoEditando, setLancamentoEditando] = useState(null);
  const [formulario, setFormulario] = useState(formularioInicial);
  const [arquivoNota, setArquivoNota] = useState(null);
  const [pesquisa, setPesquisa] = useState("");
  const [filtroMes, setFiltroMes] = useState(new Date().toISOString().slice(0, 7));
  const [limiteAlerta, setLimiteAlerta] = useState("200");
  const [limiteAlertaSalvo, setLimiteAlertaSalvo] = useState("200");
  const [salvandoLimite, setSalvandoLimite] = useState(false);
  const [dialogLimiteAberto, setDialogLimiteAberto] = useState(false);
  const [notificacao, setNotificacao] = useState({
    aberta: false,
    mensagem: "",
    tipo: "success"
  });

  const tipoUsuario = (localStorage.getItem("tipoUsuario") || "").toLowerCase();
  const podeExcluir = tipoUsuario === "admin";

  async function carregarLancamentos() {
    setCarregando(true);

    try {
      const consulta = consultaTenantAtual(
        query(collection(db, COLECAO_CAIXINHA), orderBy("data", "desc"))
      );
      let snapshot = await getDocs(consulta);
      let documentos = snapshot.docs.map((documento) => ({
        ...documento.data(),
        id: documento.id
      }));
      const configuracao = documentos.find(
        (registro) => registro.tipo === "configuracao" && registro.configuracaoCaixinha === true
      );
      const limiteConfigurado = Number(configuracao?.limiteSaldoDinheiro);
      let registros = documentos.filter(
        (registro) => registro.tipo === "entrada" || registro.tipo === "saida"
      );

      const limiteFormatado = String(
        Number.isFinite(limiteConfigurado) && limiteConfigurado >= 0
          ? limiteConfigurado
          : 200
      ).replace(".", ",");
      setLimiteAlerta(limiteFormatado);
      setLimiteAlertaSalvo(limiteFormatado);

      const mesAtual = new Date().toISOString().slice(0, 7);
      const saldoInicialCriado = await garantirSaldoInicialDoMes(registros, mesAtual, configuracao?.saldosAnterioresExcluidos);

      if (saldoInicialCriado) {
        snapshot = await getDocs(consulta);
        documentos = snapshot.docs.map((documento) => ({
          ...documento.data(),
          id: documento.id
        }));
        registros = documentos.filter(
          (registro) => registro.tipo === "entrada" || registro.tipo === "saida"
        );
      }

      setLancamentos(registros);
    } catch (error) {
      console.error("Erro ao carregar caixinha:", error);
      setNotificacao({
        aberta: true,
        mensagem: "Não foi possível carregar os lançamentos do caixinha.",
        tipo: "error"
      });
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregarLancamentos();
  }, []);

  async function salvarLimiteAlerta() {
    const limite = parseValor(limiteAlerta);

    if (!String(limiteAlerta).trim() || !Number.isFinite(limite) || limite < 0) {
      setNotificacao({
        aberta: true,
        mensagem: "Informe um limite válido, igual ou maior que zero.",
        tipo: "warning"
      });
      return;
    }

    setSalvandoLimite(true);

    try {
      const tenantId = String(localStorage.getItem("tenantId") || auth.currentUser?.uid || "geral")
        .replace(/[^a-zA-Z0-9_-]/g, "_");

      await setDoc(
        doc(db, COLECAO_CAIXINHA, `configuracao-alerta-${tenantId}`),
        aplicarTenantAtual({
          tipo: "configuracao",
          configuracaoCaixinha: true,
          data: "0000-00-00",
          limiteSaldoDinheiro: limite,
          atualizadoEm: serverTimestamp(),
          atualizadoPor: auth.currentUser?.uid || null
        }),
        { merge: true }
      );

      const limiteFormatado = String(limite).replace(".", ",");
      setLimiteAlerta(limiteFormatado);
      setLimiteAlertaSalvo(limiteFormatado);
      setDialogLimiteAberto(false);
      setNotificacao({
        aberta: true,
        mensagem: "Limite do aviso salvo com sucesso.",
        tipo: "success"
      });
    } catch (error) {
      console.error("Erro ao salvar limite do caixinha:", error);
      setNotificacao({
        aberta: true,
        mensagem: "Não foi possível salvar o limite do aviso.",
        tipo: "error"
      });
    } finally {
      setSalvandoLimite(false);
    }
  }

  function fecharDialogLimite() {
    if (salvandoLimite) return;
    setLimiteAlerta(limiteAlertaSalvo);
    setDialogLimiteAberto(false);
  }

  async function garantirSaldoInicialDoMes(registros, mes, saldosExcluidos = []) {
    if (mes < MES_INICIO_CONTROLE) return false;

    const registrosBase =
      mes === MES_INICIO_CONTROLE
        ? registros.filter((lancamento) => String(lancamento.data || "") < `${mes}-01`)
        : registros.filter((lancamento) => String(lancamento.data || "").startsWith(mesAnterior(mes)));
    const saldosBase = calcularResumo(registrosBase);
    const tenantId = String(localStorage.getItem("tenantId") || auth.currentUser?.uid || "geral")
      .replace(/[^a-zA-Z0-9_-]/g, "_");
    let criouSaldo = false;

    for (const carteira of Object.keys(CARTEIRAS)) {
      if (Array.isArray(saldosExcluidos) && saldosExcluidos.includes(`${mes}-${carteira}`)) continue;
      const saldoExistente = registros.some(
        (lancamento) =>
          String(lancamento.data || "").startsWith(mes) &&
          lancamento.tipo === "entrada" &&
          lancamento.tipoEntrada === "saldo_anterior" &&
          lancamento.carteira === carteira
      );
      const valor = Number(saldosBase[carteira] || 0);

      if (saldoExistente || valor <= 0) continue;

      await setDoc(
        doc(db, COLECAO_CAIXINHA, `saldo-anterior-${tenantId}-${mes}-${carteira}`),
        aplicarTenantAtual({
          tipo: "entrada",
          tipoEntrada: "saldo_anterior",
          carteira,
          data: `${mes}-01`,
          descricao: descricaoSaldoAnterior(carteira),
          valor,
          numeroNota: "",
          saldoAutomatico: true,
          mesReferencia: mes,
          criadoEm: serverTimestamp(),
          criadoPor: auth.currentUser?.uid || null,
          atualizadoEm: serverTimestamp(),
          atualizadoPor: auth.currentUser?.uid || null
        })
      );
      criouSaldo = true;
    }

    return criouSaldo;
  }

  const lancamentosFiltrados = useMemo(() => {
    const termo = pesquisa.trim().toLocaleLowerCase("pt-BR");

    return lancamentos
      .filter((lancamento) => {
        const correspondeMes = !filtroMes || String(lancamento.data || "").startsWith(filtroMes);
        const textoBusca = [
          lancamento.descricao,
          lancamento.numeroNota,
          TIPOS_ENTRADA[lancamento.tipoEntrada],
          CARTEIRAS[lancamento.carteira]
        ]
          .join(" ")
          .toLocaleLowerCase("pt-BR");

        return correspondeMes && (!termo || textoBusca.includes(termo));
      })
      .sort((a, b) => {
        const comparacaoData = String(b.data || "").localeCompare(String(a.data || ""));
        if (comparacaoData !== 0) return comparacaoData;

        const criadoA = a.criadoEm?.seconds || 0;
        const criadoB = b.criadoEm?.seconds || 0;
        return criadoB - criadoA;
      });
  }, [filtroMes, lancamentos, pesquisa]);

  const resumoPeriodo = useMemo(
    () => calcularResumo(lancamentosFiltrados),
    [lancamentosFiltrados]
  );
  const saldosAtuais = resumoPeriodo;

  function atualizarCampo(campo, valor) {
    setFormulario((atual) => {
      const proximo = { ...atual, [campo]: valor };

      if (campo === "tipoEntrada" && proximo.tipo === "entrada" && valor !== "saldo_anterior") {
        proximo.carteira = carteiraDaEntrada(valor);
      }

      if (campo === "tipo") {
        proximo.carteira =
          valor === "entrada" && proximo.tipoEntrada !== "saldo_anterior"
            ? carteiraDaEntrada(proximo.tipoEntrada)
            : proximo.carteira;
      }

      return proximo;
    });
  }

  function abrirNovoLancamento(tipo = "saida") {
    const tipoEntrada = tipo === "entrada" ? "deposito" : formularioInicial.tipoEntrada;
    setLancamentoEditando(null);
    setFormulario({
      ...formularioInicial,
      tipo,
      tipoEntrada,
      carteira: tipo === "entrada" ? carteiraDaEntrada(tipoEntrada) : "dinheiro"
    });
    setArquivoNota(null);
    setDialogAberto(true);
  }

  function abrirEdicao(lancamento) {
    setLancamentoEditando(lancamento);
    setFormulario({
      tipo: lancamento.tipo || "saida",
      tipoEntrada: lancamento.tipoEntrada || "deposito",
      carteira: lancamento.carteira || "dinheiro",
      data: lancamento.data || formularioInicial.data,
      descricao: lancamento.descricao || "",
      valor: String(lancamento.valor || "").replace(".", ","),
      numeroNota: lancamento.numeroNota || ""
    });
    setArquivoNota(null);
    setDialogAberto(true);
  }

  async function enviarNotaFiscal(lancamentoId) {
    if (!arquivoNota) return null;

    const contentType = arquivoNota.type || "application/octet-stream";
    const storagePath = `caixinha/notas-fiscais/${lancamentoId}/${Date.now()}-${nomeArquivoSeguro(
      arquivoNota.name
    )}`;
    const storageRef = ref(storage, storagePath);

    await uploadBytes(storageRef, arquivoNota, { contentType });
    const url = await getDownloadURL(storageRef);

    return {
      nome: arquivoNota.name,
      contentType,
      storagePath,
      url
    };
  }

  async function salvarLancamento() {
    const valor = parseValor(formulario.valor);
    const isSaldoAnterior =
      formulario.tipo === "entrada" && formulario.tipoEntrada === "saldo_anterior";
    const carteira =
      formulario.tipo === "entrada" && !isSaldoAnterior
        ? carteiraDaEntrada(formulario.tipoEntrada)
        : formulario.carteira;
    const descricao = formulario.descricao.trim() || (isSaldoAnterior ? descricaoSaldoAnterior(carteira) : "");

    if (!formulario.data) {
      setNotificacao({
        aberta: true,
        mensagem: "Informe a data do lançamento.",
        tipo: "warning"
      });
      return;
    }

    if (!descricao) {
      setNotificacao({
        aberta: true,
        mensagem: "Informe a descrição do lançamento.",
        tipo: "warning"
      });
      return;
    }

    if (!valor || valor <= 0) {
      setNotificacao({
        aberta: true,
        mensagem: "Informe um valor maior que zero.",
        tipo: "warning"
      });
      return;
    }

    setSalvando(true);

    try {
      const dados = {
        tipo: formulario.tipo,
        tipoEntrada: formulario.tipo === "entrada" ? formulario.tipoEntrada : null,
        carteira,
        data: formulario.data,
        descricao,
        valor,
        numeroNota: formulario.numeroNota.trim(),
        atualizadoEm: serverTimestamp(),
        atualizadoPor: auth.currentUser?.uid || null
      };

      let lancamentoId = lancamentoEditando?.id;

      if (lancamentoEditando) {
        await updateDoc(doc(db, COLECAO_CAIXINHA, lancamentoEditando.id), dados);
      } else {
        const novo = await addDoc(collection(db, COLECAO_CAIXINHA), aplicarTenantAtual({
          ...dados,
          criadoEm: serverTimestamp(),
          criadoPor: auth.currentUser?.uid || null
        }));
        lancamentoId = novo.id;
      }

      const notaFiscal = await enviarNotaFiscal(lancamentoId);

      if (notaFiscal) {
        await updateDoc(doc(db, COLECAO_CAIXINHA, lancamentoId), { notaFiscal });

        if (
          lancamentoEditando?.notaFiscal?.storagePath &&
          lancamentoEditando.notaFiscal.storagePath !== notaFiscal.storagePath
        ) {
          deleteObject(ref(storage, lancamentoEditando.notaFiscal.storagePath)).catch(() => {});
        }
      }

      setDialogAberto(false);
      setNotificacao({
        aberta: true,
        mensagem: lancamentoEditando
        ? "Lançamento atualizado com sucesso."
        : "Lançamento cadastrado com sucesso.",
        tipo: "success"
      });
      await carregarLancamentos();
    } catch (error) {
      console.error("Erro ao salvar lancamento do caixinha:", error);
      setNotificacao({
        aberta: true,
      mensagem: "Não foi possível salvar o lançamento.",
        tipo: "error"
      });
    } finally {
      setSalvando(false);
    }
  }

  async function excluirLancamento(lancamento) {
    if (!podeExcluir) return;
    if (!(await confirmarSistema("Deseja excluir este lançamento do caixinha?"))) return;

    try {
      const lote = writeBatch(db);
      lote.delete(doc(db, COLECAO_CAIXINHA, lancamento.id));

      if (lancamento.tipo === "entrada" && lancamento.tipoEntrada === "saldo_anterior") {
        const tenantId = String(localStorage.getItem("tenantId") || auth.currentUser?.uid || "geral")
          .replace(/[^a-zA-Z0-9_-]/g, "_");
        const mes = String(lancamento.data || "").slice(0, 7);
        lote.set(
          doc(db, COLECAO_CAIXINHA, `configuracao-alerta-${tenantId}`),
          aplicarTenantAtual({
            tipo: "configuracao",
            configuracaoCaixinha: true,
            data: "0000-00-00",
            saldosAnterioresExcluidos: arrayUnion(`${mes}-${lancamento.carteira}`),
            atualizadoEm: serverTimestamp(),
            atualizadoPor: auth.currentUser?.uid || null
          }),
          { merge: true }
        );
      }

      await lote.commit();
      setLancamentos((anteriores) => anteriores.filter((item) => item.id !== lancamento.id));

      if (lancamento.notaFiscal?.storagePath) {
        deleteObject(ref(storage, lancamento.notaFiscal.storagePath)).catch(() => {});
      }

      setNotificacao({
        aberta: true,
      mensagem: "Lançamento excluído com sucesso.",
        tipo: "success"
      });
      await carregarLancamentos();
    } catch (error) {
      console.error("Erro ao excluir lancamento:", error);
      setNotificacao({
        aberta: true,
      mensagem: "Não foi possível excluir o lançamento.",
        tipo: "error"
      });
    }
  }

  function abrirNotaFiscal(lancamento) {
    if (lancamento.notaFiscal?.url) {
      window.open(lancamento.notaFiscal.url, "_blank", "noopener,noreferrer");
    }
  }

  const seletorCarteira = (titulo) => (
    <Box>
      <Typography fontWeight={700} mb={0.5}>
        {titulo}
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formulario.carteira === "dinheiro"}
              onChange={() => atualizarCampo("carteira", "dinheiro")}
            />
          }
          label="Dinheiro do caixinha"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formulario.carteira === "cartao"}
              onChange={() => atualizarCampo("carteira", "cartao")}
            />
          }
            label="Cartão alimentação"
        />
      </Stack>
    </Box>
  );

  return (
    <>
      <Navbar
        open={open}
        isMobile={isMobile}
        userName={auth.currentUser?.displayName || auth.currentUser?.email || ""}
      />

      <Box sx={{ p: { xs: 2, md: 4 }, mt: 6 }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={2}
          mb={3}
        >
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Caixinha da Empresa
            </Typography>
            <Typography color="text.secondary">
              Controle separado de dinheiro em caixa e crédito do cartão alimentação.
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Tooltip title="Configurar limite do aviso">
              <IconButton
                color="primary"
                onClick={() => setDialogLimiteAberto(true)}
                aria-label="Configurar limite do aviso de saldo baixo"
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Button variant="outlined" onClick={() => abrirNovoLancamento("entrada")}>
              + Entrada
            </Button>
            <Button variant="contained" onClick={() => abrirNovoLancamento("saida")}>
              + Despesa
            </Button>
          </Stack>
        </Stack>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" },
            gap: 2,
            mb: 3
          }}
        >
          {[
            { label: "Saldo dinheiro atual", value: saldosAtuais.dinheiro, color: "#1b5e20" },
            { label: "Saldo cartão atual", value: saldosAtuais.cartao, color: "#0d47a1" },
            { label: "Entradas no período", value: resumoPeriodo.totalEntradas, color: "#2e7d32" },
            { label: "Saídas no período", value: resumoPeriodo.totalSaidas, color: "#b71c1c" },
            { label: "Saldo do período", value: resumoPeriodo.saldo, color: "#263238" }
          ].map((item) => (
            <Paper key={item.label} sx={{ p: 2, borderRadius: 1 }}>
              <Typography color="text.secondary" fontSize={13}>
                {item.label}
              </Typography>
              <Typography fontSize={24} fontWeight={900} color={item.color}>
                {moeda(item.value)}
              </Typography>
            </Paper>
          ))}
        </Box>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          mb={2}
          alignItems={{ md: "center" }}
        >
          <TextField
            type="month"
            label="Mês"
            value={filtroMes}
            onChange={(event) => setFiltroMes(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="Pesquisar por descrição ou nota"
            value={pesquisa}
            onChange={(event) => setPesquisa(event.target.value)}
            sx={{ flex: 1 }}
          />
          <Button variant="text" onClick={() => setFiltroMes("")}>
            Ver todos
          </Button>
        </Stack>

        <Paper sx={{ overflowX: "auto", borderRadius: 1 }}>
          <Table className="lista-cadastro-padrao">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Carteira</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Nota fiscal</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell width={150}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {carregando && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}

              {!carregando && lancamentosFiltrados.length === 0 && (
                <TableRow>
                <TableCell colSpan={7}>Nenhum lançamento encontrado.</TableCell>
                </TableRow>
              )}

              {lancamentosFiltrados.map((lancamento) => (
                <TableRow key={lancamento.id}>
                  <TableCell className="lista-dado-secundario">{formatarDataBR(lancamento.data)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={lancamento.tipo === "entrada" ? "success" : "error"}
                      label={
                        lancamento.tipo === "entrada"
                          ? TIPOS_ENTRADA[lancamento.tipoEntrada] || "Entrada"
                          : "Despesa"
                      }
                    />
                  </TableCell>
                  <TableCell className="lista-dado-principal">{CARTEIRAS[lancamento.carteira] || "-"}</TableCell>
                  <TableCell className="lista-dado-principal">{lancamento.descricao}</TableCell>
                  <TableCell>
                    {lancamento.notaFiscal?.url ? (
                      <Button
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => abrirNotaFiscal(lancamento)}
                      >
                        {lancamento.numeroNota || "Abrir nota"}
                      </Button>
                    ) : (
                      lancamento.numeroNota || "-"
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      fontWeight={800}
                      color={lancamento.tipo === "entrada" ? "success.main" : "error.main"}
                    >
                      {lancamento.tipo === "entrada" ? "+" : "-"} {moeda(lancamento.valor)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => abrirEdicao(lancamento)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {podeExcluir && (
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => excluirLancamento(lancamento)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <HistoricoCadastro colecao={COLECAO_CAIXINHA} documentoId={lancamento.id} registro={lancamento} />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog
        open={dialogLimiteAberto}
        onClose={fecharDialogLimite}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Configurar aviso de saldo baixo</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            label="Limite do saldo em dinheiro"
            value={limiteAlerta}
            onChange={(event) => setLimiteAlerta(event.target.value)}
            inputProps={{ inputMode: "decimal" }}
            helperText="O admin SaaS será avisado no login quando o saldo ficar abaixo deste valor."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={fecharDialogLimite}
            disabled={salvandoLimite}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={salvarLimiteAlerta}
            disabled={salvandoLimite}
          >
            {salvandoLimite ? <CircularProgress size={22} /> : "Salvar limite"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogAberto}
        onClose={() => !salvando && setDialogAberto(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {lancamentoEditando ? "Editar lançamento" : "Novo lançamento"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
                label="Tipo de lançamento"
              value={formulario.tipo}
              onChange={(event) => atualizarCampo("tipo", event.target.value)}
              fullWidth
            >
              <MenuItem value="entrada">Entrada</MenuItem>
              <MenuItem value="saida">Despesa</MenuItem>
            </TextField>

            {formulario.tipo === "entrada" ? (
              <TextField
                select
                  label="Tipo de entrada"
                value={formulario.tipoEntrada}
                onChange={(event) => atualizarCampo("tipoEntrada", event.target.value)}
                fullWidth
              >
                  <MenuItem value="deposito">Depósito para repor caixinha</MenuItem>
                  <MenuItem value="cartao_alimentacao">Crédito no cartão alimentação</MenuItem>
                <MenuItem value="saldo_anterior">Saldo anterior</MenuItem>
              </TextField>
            ) : (
              seletorCarteira("A despesa foi paga com:")
            )}

            {formulario.tipo === "entrada" &&
              formulario.tipoEntrada === "saldo_anterior" &&
              seletorCarteira("Saldo anterior de:")}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                type="date"
                label="Data"
                value={formulario.data}
                onChange={(event) => atualizarCampo("data", event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Valor"
                value={formulario.valor}
                onChange={(event) => atualizarCampo("valor", event.target.value)}
                placeholder="0,00"
                fullWidth
              />
            </Stack>

            <TextField
                label="Descrição"
              value={formulario.descricao}
              onChange={(event) => atualizarCampo("descricao", event.target.value)}
              placeholder={
                formulario.tipo === "entrada" && formulario.tipoEntrada === "saldo_anterior"
                    ? "Opcional. Se ficar em branco, será preenchido automaticamente."
                  : ""
              }
              fullWidth
              multiline
              minRows={2}
            />

            <TextField
                label="Número da nota fiscal / comprovante"
              value={formulario.numeroNota}
              onChange={(event) => atualizarCampo("numeroNota", event.target.value)}
              fullWidth
            />

            <Divider />

            <Stack spacing={1}>
              <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                Anexar nota fiscal
                <input
                  hidden
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={(event) => setArquivoNota(event.target.files?.[0] || null)}
                />
              </Button>
              <Typography color="text.secondary" fontSize={13}>
                {arquivoNota?.name ||
                  lancamentoEditando?.notaFiscal?.nome ||
                  "PDF ou imagem da nota fiscal."}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAberto(false)} disabled={salvando}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={salvarLancamento} disabled={salvando}>
            {salvando ? <CircularProgress size={22} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notificacao.aberta}
        autoHideDuration={5000}
        onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={notificacao.tipo}
          variant="filled"
          onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}
        >
          {notificacao.mensagem}
        </Alert>
      </Snackbar>
    </>
  );
}
